import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ShieldCheck } from "lucide-react";
import { SITE } from "@/data/site";
import { getServices } from "@/data/services";
import { getAreas } from "@/data/areas";
import { useLang } from "@/lib/i18n";

export default function Footer() {
  const { t, lang } = useLang();
  const SERVICES = getServices(lang);
  const AREAS = getAreas(lang);

  return (
    <footer data-testid="site-footer" className="bg-brand-navy-dark text-slate-200">
      <div className="container-max py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <img src={SITE.logo} alt="Faster Plumbing" className="h-16 w-16 object-contain bg-white/5 rounded-xl p-1" />
            <div className="leading-tight">
              <p className="font-heading text-lg font-bold text-white">Faster Plumbing</p>
              <p className="text-xs text-brand-yellow uppercase tracking-[0.18em] font-semibold">12 {lang === "es" ? "Años" : "Years"} · 24/7</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-400 leading-relaxed">
            {lang === "es"
              ? "La compañía de plomería de confianza del Sur de Florida desde 2014. Residencial, comercial y nueva construcción en Palm Beach, Broward, Miami-Dade y Martin."
              : "South Florida's trusted plumbing company since 2014. Residential, commercial, and new construction across Palm Beach, Broward, Miami-Dade & Martin counties."}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-brand-yellow font-semibold">
            <ShieldCheck size={14} />
            {t.common.licensedInsured} · #{SITE.license}
          </div>
        </div>

        <div>
          <h4 className="text-white font-heading font-semibold mb-4">{t.nav.services}</h4>
          <ul className="space-y-2 text-sm">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} data-testid={`footer-service-${s.slug}`}
                  className="text-slate-400 hover:text-brand-yellow transition-colors">
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-heading font-semibold mb-4">{t.nav.areas}</h4>
          <ul className="space-y-2 text-sm">
            {AREAS.map((a) => (
              <li key={a.slug}>
                <Link to={`/service-areas/${a.slug}`} data-testid={`footer-area-${a.slug}`}
                  className="text-slate-400 hover:text-brand-yellow transition-colors">
                  {a.name}
                </Link>
              </li>
            ))}
          </ul>
          <h4 className="text-white font-heading font-semibold mt-6 mb-3">{lang === "es" ? "Compañía" : "Company"}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="text-slate-400 hover:text-brand-yellow">{t.nav.about}</Link></li>
            <li><Link to="/contact" className="text-slate-400 hover:text-brand-yellow">{t.nav.contact}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-heading font-semibold mb-4">{t.nav.contact}</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <Phone size={16} className="mt-0.5 text-brand-yellow" />
              <div>
                <a href={`tel:${SITE.primaryPhone}`} className="block text-white font-semibold hover:text-brand-yellow">{SITE.primaryPhone}</a>
                <a href={`tel:${SITE.secondaryPhone}`} className="block text-slate-400 hover:text-brand-yellow">{SITE.secondaryPhone}</a>
                <a href={`tel:${SITE.tertiaryPhone}`} className="block text-slate-400 hover:text-brand-yellow">{SITE.tertiaryPhone}</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={16} className="mt-0.5 text-brand-yellow" />
              <a href={`mailto:${SITE.email}`} className="text-slate-300 hover:text-brand-yellow break-all">{SITE.email}</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={16} className="mt-0.5 text-brand-yellow" />
              <span className="text-slate-300">{SITE.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={16} className="mt-0.5 text-brand-yellow" />
              <span className="text-slate-300">{SITE.hours}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-max py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} Faster Plumbing. {lang === "es" ? "Todos los derechos reservados." : "All rights reserved."}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 justify-center">
            <p>Lake Worth Beach, FL · {lang === "es" ? "Servimos" : "Serving"} Palm Beach, Broward, Miami-Dade & Martin</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 justify-center">
            <Link
              to="/terms"
              data-testid="footer-terms-link"
              className="hover:text-brand-yellow transition-colors"
            >
              {lang === "es" ? "Términos y Condiciones" : "Terms & Conditions"}
            </Link>
            <span className="opacity-40">·</span>
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="hover:text-brand-yellow transition-colors"
            >
              {lang === "es" ? "Política de Privacidad" : "Privacy Policy"}
            </Link>
            <span className="opacity-40">·</span>
            <a
              href="https://app.emergent.sh/?utm_source=emergent-badge"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-emergent-link"
              className="hover:text-brand-yellow transition-colors"
            >
              {lang === "es" ? "Hecho con Emergent" : "Made with Emergent"}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
